// Reading editions: the tune as a transposing or bass-clef player reads it.
//
// A "B♭ version" of a lead sheet is the same tune written a major 2nd above
// concert pitch on a plain G clef, so a trumpet or tenor player sounds the
// concert changes while reading their own key — the Real Book ships a volume
// per edition (C, B♭, E♭, bass clef) and the changes are the SAME music. An
// edition here is a READING transform, never an edit: the Score the store
// holds stays in concert pitch and the band plays from it, and this module
// derives the written view the sheet draws. `editionScore` returns a whole
// Score rather than patching the engraver because chord symbols, key
// signature, melody, clef and the analysis lane then all follow from ONE
// transform through the ordinary projections (scoreToChart, scoreToSong) —
// the chart surface and the notation cannot disagree about a spelling.
//
// The interval is diatonic (`transposePitch`): E♭ + M2 = F, never E♯, and
// the written key follows the rule the transposing-part machinery already
// uses (`writtenKeySignature`). Where the letter answer lands the KEY on an
// unprintable tonic — concert B read by a B♭ player is C♯ major by the
// letter, seven sharps — the interval bends one letter step to the enharmonic
// the book prints (D♭), and every note and symbol follows the same bent
// interval, which is exactly how MusicXML's <transpose> pairs its diatonic
// and chromatic fields.
//
// C bass is concert pitch in the bass clef an octave down — the Bass Clef
// Real Book's register: a lead-sheet melody C4–E5 lands on the bass staff as
// C3–E4 — applied to treble-family staves ONLY. A staff already in the bass
// clef (a piano's left hand, a written bass part) is already what a bass-clef
// reader wants and stays put; a tab staff belongs to a guitar and never
// transposes. Chord symbols and the key signature are unchanged, since an
// octave moves no pitch class.
//
// A part carrying its own `transposition` (an imported B♭ trumpet part) is
// OVERRIDDEN, not composed with: the edition says "read the whole score as a
// B♭ player would", so every staff is concert + the edition interval and the
// view carries no part transposition of its own ("for multiple parts or piano
// just transpose and indicate", user ruling Sep 2026). `'c'` is the default
// and returns the score itself, so a transposing part keeps its own written
// view there, as it always has.

import { chartChordSymbol } from '../chart';
import type { IRealChordRef } from '../ireal/types';
import { accidentalForKey, parseKey, type Accidental } from '../notes';
import { keyLabelOf } from './convert';
import {
  midiOf,
  noteNameOf,
  parseSpelledPitchName,
  spellMidi,
  transposePitch,
  type DiatonicInterval,
  type SpelledPitch,
  type Step,
} from './pitch';
import { writtenKeySignature } from './toSong';
import type {
  Clef,
  FormMeasure,
  HarmonySymbol,
  KeySignature,
  NoteHead,
  Part,
  Score,
  StaffDef,
  VoiceItem,
} from './types';

export type ScoreEdition = 'c' | 'bb' | 'eb' | 'c-bass';

export interface ScoreEditionDef {
  id: ScoreEdition;
  /** Picker label. */
  label: string;
  /** Printed on the sheet's masthead; null for the C book, which prints nothing. */
  sheetLabel: string | null;
  /** written = concert + interval, before the key-conventional bend. */
  interval: DiatonicInterval;
  /** Clef a treble-family staff takes in this edition; null = as it stands. */
  clef: Extract<Clef, 'treble' | 'bass'> | null;
  /** Who reads it — the picker's hint. */
  hint: string;
}

export const SCORE_EDITIONS: readonly ScoreEditionDef[] = [
  {
    id: 'c',
    label: 'C treble',
    sheetLabel: null,
    interval: { degrees: 0, semitones: 0 },
    clef: null,
    hint: 'Guitar, piano, flute, violin · concert pitch',
  },
  {
    id: 'bb',
    label: 'B♭',
    sheetLabel: 'B♭ version',
    interval: { degrees: 1, semitones: 2 },
    clef: 'treble',
    hint: 'Trumpet, tenor sax, clarinet · major 2nd ↑',
  },
  {
    id: 'eb',
    label: 'E♭',
    sheetLabel: 'E♭ version',
    interval: { degrees: 5, semitones: 9 },
    clef: 'treble',
    hint: 'Alto sax, baritone sax · major 6th ↑',
  },
  {
    id: 'c-bass',
    label: 'C bass',
    sheetLabel: 'C bass version',
    interval: { degrees: -7, semitones: -12 },
    clef: 'bass',
    hint: 'Trombone, bass, cello · bass clef, octave ↓',
  },
];

export const DEFAULT_SCORE_EDITION: ScoreEdition = 'c';

export function isScoreEdition(value: unknown): value is ScoreEdition {
  return typeof value === 'string' && SCORE_EDITIONS.some((e) => e.id === value);
}

export function scoreEditionDef(id: ScoreEdition): ScoreEditionDef {
  return SCORE_EDITIONS.find((e) => e.id === id) ?? SCORE_EDITIONS[0];
}

/** The masthead's "B♭ version"; null for the C book. */
export function editionSheetLabel(id: ScoreEdition): string | null {
  return scoreEditionDef(id).sheetLabel;
}

const UNISON: DiatonicInterval = { degrees: 0, semitones: 0 };
const isUnison = (iv: DiatonicInterval) => iv.degrees === 0 && iv.semitones === 0;
/** An octave (or unison) moves no pitch class: symbols and key stand. */
const keepsPitchClasses = (iv: DiatonicInterval) => iv.semitones % 12 === 0;

/** Sharps (+) or flats (−) in a key signature on this tonic — F♯ major is 6,
 *  C♯ major 7, G♭ major −6, C♭ major −7; a minor key sits three fifths below
 *  its tonic's major. */
const STEP_FIFTHS: Record<Step, number> = { F: -1, C: 0, G: 1, D: 2, A: 3, E: 4, B: 5 };
function keyFifths(tonic: SpelledPitch, mode: 'major' | 'minor'): number {
  return STEP_FIFTHS[tonic.step] + 7 * tonic.alter - (mode === 'minor' ? 3 : 0);
}

function tonicPitch(sig: KeySignature): SpelledPitch | null {
  try {
    return { ...parseSpelledPitchName(sig.tonic), octave: 4 };
  } catch {
    return null;
  }
}

/**
 * The interval the whole score moves by for an edition: the edition's own,
 * bent one letter step where the letter answer puts the opening KEY beyond six
 * accidentals (C♯ major → D♭, G♯ → A♭, C♭ → B). Bending the interval rather
 * than respelling the key alone is what keeps every note and chord symbol in
 * the same spelling as the signature they sit under.
 */
export function editionInterval(score: Score, edition: ScoreEdition): DiatonicInterval {
  const base = scoreEditionDef(edition).interval;
  if (keepsPitchClasses(base)) return base;
  const sig = score.form[0]?.keySig;
  const tonic = sig ? tonicPitch(sig) : null;
  if (!sig || !tonic) return base;
  let written: SpelledPitch;
  try {
    written = transposePitch(tonic, base);
  } catch {
    return base;
  }
  const fifths = keyFifths(written, sig.mode);
  const bend = fifths > 6 ? 1 : fifths < -6 ? -1 : 0;
  return bend === 0 ? base : { degrees: base.degrees + bend, semitones: base.semitones };
}

function transposeHeadBy(head: NoteHead, iv: DiatonicInterval, prefer: Accidental): NoteHead {
  let pitch: SpelledPitch;
  try {
    pitch = transposePitch(head.pitch, iv);
  } catch {
    // The letter answer would need a triple accidental (a double-sharp input
    // moved sharper) — respell from the sound, keeping the input's side.
    const side = head.pitch.alter < 0 ? 'flat' : head.pitch.alter > 0 ? 'sharp' : prefer;
    const midi = Math.max(0, Math.min(127, midiOf(head.pitch) + iv.semitones));
    pitch = spellMidi(midi, { prefer: side });
  }
  return { ...head, pitch };
}

function transposeItemsBy(
  items: VoiceItem[],
  homeStaff: number,
  intervals: DiatonicInterval[],
  prefer: Accidental,
): VoiceItem[] {
  return items.map((item) => {
    if (item.kind === 'tuplet') {
      return { ...item, items: transposeItemsBy(item.items, homeStaff, intervals, prefer) };
    }
    // A cross-staff note moves with the staff it is DRAWN on (the C bass
    // edition moves treble staves only), so resolve the staff per event.
    const iv = intervals[item.staff ?? homeStaff] ?? intervals[0] ?? UNISON;
    return {
      ...item,
      duration: { ...item.duration },
      notes: item.notes.map((head) =>
        isUnison(iv) ? { ...head, pitch: { ...head.pitch } } : transposeHeadBy(head, iv, prefer),
      ),
    };
  });
}

/** Note name → note name ("Eb" + M2 = "F"); unparseable names pass through. */
function transposeNoteName(name: string, iv: DiatonicInterval, prefer: Accidental): string {
  let parsed: SpelledPitch;
  try {
    parsed = { ...parseSpelledPitchName(name), octave: 4 };
  } catch {
    return name;
  }
  try {
    return noteNameOf(transposePitch(parsed, iv));
  } catch {
    return noteNameOf(spellMidi(midiOf(parsed) + iv.semitones, { prefer }));
  }
}

function transposeRef<T extends IRealChordRef>(
  ref: T,
  iv: DiatonicInterval,
  prefer: Accidental,
): T {
  return {
    ...ref,
    root: transposeNoteName(ref.root, iv, prefer),
    ...(ref.bass ? { bass: transposeNoteName(ref.bass, iv, prefer) } : {}),
  };
}

function transposeSymbol(
  c: HarmonySymbol,
  iv: DiatonicInterval,
  prefer: Accidental,
): HarmonySymbol {
  if (c.noChord) return { ...c };
  const moved = transposeRef(c, iv, prefer);
  return {
    ...moved,
    ...(c.alternate ? { alternate: transposeRef(c.alternate, iv, prefer) } : {}),
    ...(c.targetTopNote ? { targetTopNote: transposeNoteName(c.targetTopNote, iv, prefer) } : {}),
    symbol: chartChordSymbol(moved),
  };
}

/** Per staff: the interval its notes move by and the clef it takes. */
function planStaves(
  staves: StaffDef[],
  def: ScoreEditionDef,
  iv: DiatonicInterval,
): { staves: StaffDef[]; intervals: DiatonicInterval[] } {
  const out: StaffDef[] = [];
  const intervals: DiatonicInterval[] = [];
  for (const staff of staves) {
    const trebleFamily = staff.clef === 'treble' || staff.clef === 'treble8vb';
    const fixed = staff.staffType === 'tab' || staff.clef === 'percussion';
    if (fixed) {
      out.push({ ...staff });
      intervals.push(UNISON);
    } else if (def.id === 'c-bass') {
      // Only what a bass-clef reader could not already read moves.
      out.push(trebleFamily ? { ...staff, clef: 'bass' } : { ...staff });
      intervals.push(trebleFamily ? iv : UNISON);
    } else {
      out.push(trebleFamily && def.clef ? { ...staff, clef: def.clef } : { ...staff });
      intervals.push(iv);
    }
  }
  return { staves: out, intervals };
}

/**
 * The score as an edition's reader sees it. Pure and total: `'c'` is the score
 * itself (same reference); any other edition is a new Score with every part's
 * pitches, the harmony lane and the key signatures moved by
 * `editionInterval`, treble-family staves reclefed for the edition, and no
 * per-part transposition left in the view. Ids are preserved throughout, so
 * chord taps, the playhead and selection keep addressing the concert truth.
 */
export function editionScore(score: Score, edition: ScoreEdition): Score {
  const def = scoreEditionDef(edition);
  if (def.id === 'c') return score;
  const iv = editionInterval(score, edition);
  const writtenOpening = writtenKeySignature(score.form[0]?.keySig, iv);
  const prefer = accidentalForKey(parseKey(keyLabelOf(writtenOpening)));

  const form: FormMeasure[] = keepsPitchClasses(iv)
    ? score.form.map((fm) => ({ ...fm }))
    : score.form.map((fm) => ({
        ...fm,
        harmony: fm.harmony.map((c) => transposeSymbol(c, iv, prefer)),
        ...(fm.keySig ? { keySig: writtenKeySignature(fm.keySig, iv) } : {}),
      }));

  const parts: Part[] = score.parts.map((part) => {
    const plan = planStaves(part.staves, def, iv);
    const view: Part = {
      ...part,
      staves: plan.staves,
      measures: part.measures.map((measure) => ({
        ...measure,
        voices: measure.voices.map((voice) => ({
          ...voice,
          items: transposeItemsBy(voice.items, voice.staff ?? 0, plan.intervals, prefer),
        })),
      })),
    };
    // The view IS the written score: no derivation left for the engraver.
    delete view.transposition;
    return view;
  });

  return { ...score, form, parts };
}
